import React from "react";
import PropTypes from "prop-types";
import PlayerPreview from "core/components/PlayerPreview";
import PlayerFooter from "core/components/PlayerFooter";
import PlayerPause from "core/components/PlayerPause";
import useAutoPlay from "core/hooks/useAutoPlay";
import useIdle from "core/hooks/useIdle";
import EmbedPrePlay from "core/components/EmbedPrePlay";

const FORMAT_PRIORITY = ["MAXRES", "HIGH", "MEDIUM", "DEFAULT"];

function getPreviewUrl(content) {
  if (content.preview) return content.preview;
  const thumbnails = content.thumbnails;
  if (!thumbnails?.length) return undefined;
  for (const format of FORMAT_PRIORITY) {
    const thumb = thumbnails.find((t) => t.format === format);
    if (thumb) return thumb.url;
  }
  return thumbnails[0]?.url;
}

export default function PreviewScreen({
  controls = true,
  content,
  onClickPlay,
  hideTitle,
  hideShare,
  hideLogo,
  disableTitleLink,
  autoPlay,
  fullscreenTarget,
  closeRouteDialogButton,
  layout = "default",
}) {
  const ref = useAutoPlay(onClickPlay, autoPlay);
  const idle = useIdle({ target: "preview" });

  if (layout === "embed") {
    return (
      <PlayerPreview preview={getPreviewUrl(content)} ref={ref} id="preview">
        {controls && !autoPlay && (
          <EmbedPrePlay
            content={content}
            onClickPlay={onClickPlay}
            hideShare={hideShare}
            hideLogo={hideLogo}
            disableTitleLink={disableTitleLink}
          />
        )}
        {closeRouteDialogButton}
      </PlayerPreview>
    );
  }

  return (
    <PlayerPreview
      preview={getPreviewUrl(content)}
      ref={ref}
      overlay={!idle}
      id="preview"
    >
      {!hideTitle && controls && (
        <PlayerPause
          title={content.title}
          contentUrl={content.shareUrl}
          onClickPlay={onClickPlay}
          visible={!autoPlay && !idle}
          transparent
          hideLogo={hideLogo || content.hideLogo}
          disableTitleLink={disableTitleLink}
        />
      )}

      {controls && (
        <PlayerFooter
          content={content}
          position="absolute"
          visible
          hideShare={hideShare}
          hideLogo={hideLogo}
          fullscreenTarget={fullscreenTarget}
        />
      )}
      {closeRouteDialogButton}
    </PlayerPreview>
  );
}

PreviewScreen.propTypes = {
  layout: PropTypes.oneOf(["default", "embed"]),
  closeRouteDialogButton: PropTypes.node,
  controls: PropTypes.bool,
  fullscreenTarget: PropTypes.string,
  hideShare: PropTypes.bool,
  hideLogo: PropTypes.bool,
  disableTitleLink: PropTypes.bool,
  autoPlay: PropTypes.bool,
  hideTitle: PropTypes.bool,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shareUrl: PropTypes.string.isRequired,
    preview: PropTypes.string,
    hideLogo: PropTypes.bool,
    rightholder: PropTypes.shape({
      isAdvEnabled: PropTypes.bool,
    }),
  }).isRequired,
  onClickPlay: PropTypes.func.isRequired,
};
